import type { EventLocation, EventVenue } from '../types/api';
import { parseEventLocalDateTime, hasKnownEventTime } from './eventDateTime';

/**
 * Time-zone resolution for event display.
 *
 * The pipeline stores every date as the *website-local wall clock* — there is
 * no stored offset or IANA zone anywhere in the schema. So we never convert a
 * time; we only decide whether we can honestly *label* it.
 *
 * Display rules (UX spec §9):
 *   - In-person events use the venue-local zone.
 *   - Virtual events use their declared event zone (none is stored today, so
 *     none is shown).
 *   - Hybrid events use the declared event zone, falling back to venue-local.
 *   - Legacy events without reliable data get no abbreviation rather than a
 *     fabricated one.
 */

export type EventFormat = 'in-person' | 'virtual' | 'hybrid';

export interface ResolvedTimeZone {
  /** IANA identifier, e.g. 'America/Los_Angeles'. Null when unresolvable. */
  timeZone: string | null;
  /** Short abbreviation for the event's own date, e.g. 'PDT'. Null when unresolvable. */
  abbreviation: string | null;
  /** Which signal produced the zone — useful for debugging and tests. */
  source: 'venue' | 'location' | null;
}

const UNRESOLVED: ResolvedTimeZone = { timeZone: null, abbreviation: null, source: null };

/** US states and territories that sit entirely inside a single zone. */
const US_SINGLE_ZONE: Record<string, string> = {
  alabama: 'America/Chicago',
  arizona: 'America/Phoenix',
  arkansas: 'America/Chicago',
  california: 'America/Los_Angeles',
  colorado: 'America/Denver',
  connecticut: 'America/New_York',
  delaware: 'America/New_York',
  'district of columbia': 'America/New_York',
  georgia: 'America/New_York',
  hawaii: 'Pacific/Honolulu',
  illinois: 'America/Chicago',
  iowa: 'America/Chicago',
  louisiana: 'America/Chicago',
  maine: 'America/New_York',
  maryland: 'America/New_York',
  massachusetts: 'America/New_York',
  minnesota: 'America/Chicago',
  mississippi: 'America/Chicago',
  missouri: 'America/Chicago',
  montana: 'America/Denver',
  nevada: 'America/Los_Angeles',
  'new hampshire': 'America/New_York',
  'new jersey': 'America/New_York',
  'new mexico': 'America/Denver',
  'new york': 'America/New_York',
  'north carolina': 'America/New_York',
  ohio: 'America/New_York',
  oklahoma: 'America/Chicago',
  pennsylvania: 'America/New_York',
  'puerto rico': 'America/Puerto_Rico',
  'rhode island': 'America/New_York',
  'south carolina': 'America/New_York',
  utah: 'America/Denver',
  vermont: 'America/New_York',
  virginia: 'America/New_York',
  washington: 'America/Los_Angeles',
  'west virginia': 'America/New_York',
  wisconsin: 'America/Chicago',
  wyoming: 'America/Denver',
};

/**
 * States that straddle a zone boundary. Resolved by longitude/latitude when the
 * event has coordinates; otherwise we decline to guess.
 */
const US_SPLIT_ZONE: Record<string, (lat: number, lng: number) => string> = {
  alaska: () => 'America/Anchorage',
  florida: (_lat, lng) => (lng < -85 ? 'America/Chicago' : 'America/New_York'),
  idaho: (lat) => (lat >= 45.5 ? 'America/Los_Angeles' : 'America/Boise'),
  indiana: (_lat, lng) => (lng < -87.2 ? 'America/Chicago' : 'America/Indiana/Indianapolis'),
  kansas: (_lat, lng) => (lng < -101.5 ? 'America/Denver' : 'America/Chicago'),
  kentucky: (_lat, lng) => (lng < -85.3 ? 'America/Chicago' : 'America/New_York'),
  michigan: (_lat, lng) => (lng < -87 ? 'America/Chicago' : 'America/Detroit'),
  nebraska: (_lat, lng) => (lng < -101.5 ? 'America/Denver' : 'America/Chicago'),
  'north dakota': (_lat, lng) => (lng < -101 ? 'America/Denver' : 'America/Chicago'),
  oregon: (_lat, lng) => (lng < -117.5 ? 'America/Boise' : 'America/Los_Angeles'),
  'south dakota': (_lat, lng) => (lng < -100 ? 'America/Denver' : 'America/Chicago'),
  tennessee: (_lat, lng) => (lng < -85.3 ? 'America/Chicago' : 'America/New_York'),
  texas: (_lat, lng) => (lng < -104.9 ? 'America/Denver' : 'America/Chicago'),
};

const CANADA_ZONE: Record<string, string> = {
  alberta: 'America/Edmonton',
  'british columbia': 'America/Vancouver',
  manitoba: 'America/Winnipeg',
  'new brunswick': 'America/Moncton',
  'newfoundland and labrador': 'America/St_Johns',
  'nova scotia': 'America/Halifax',
  ontario: 'America/Toronto',
  'prince edward island': 'America/Halifax',
  quebec: 'America/Toronto',
  québec: 'America/Toronto',
  saskatchewan: 'America/Regina',
  yukon: 'America/Whitehorse',
};

/** Country-level defaults for single-zone (or overwhelmingly single-zone) countries. */
const COUNTRY_ZONE: Record<string, string> = {
  argentina: 'America/Argentina/Buenos_Aires',
  bahamas: 'America/Nassau',
  barbados: 'America/Barbados',
  belize: 'America/Belize',
  bolivia: 'America/La_Paz',
  colombia: 'America/Bogota',
  'costa rica': 'America/Costa_Rica',
  cuba: 'America/Havana',
  'dominican republic': 'America/Santo_Domingo',
  ecuador: 'America/Guayaquil',
  'el salvador': 'America/El_Salvador',
  guatemala: 'America/Guatemala',
  guyana: 'America/Guyana',
  haiti: 'America/Port-au-Prince',
  honduras: 'America/Tegucigalpa',
  jamaica: 'America/Jamaica',
  nicaragua: 'America/Managua',
  panama: 'America/Panama',
  paraguay: 'America/Asuncion',
  peru: 'America/Lima',
  'puerto rico': 'America/Puerto_Rico',
  suriname: 'America/Paramaribo',
  'trinidad and tobago': 'America/Port_of_Spain',
  uruguay: 'America/Montevideo',
  venezuela: 'America/Caracas',

  // --- Europe ---
  albania: 'Europe/Tirane',
  armenia: 'Asia/Yerevan',
  austria: 'Europe/Vienna',
  azerbaijan: 'Asia/Baku',
  belarus: 'Europe/Minsk',
  belgium: 'Europe/Brussels',
  'bosnia and herzegovina': 'Europe/Sarajevo',
  bulgaria: 'Europe/Sofia',
  croatia: 'Europe/Zagreb',
  czechia: 'Europe/Prague',
  'czech republic': 'Europe/Prague',
  denmark: 'Europe/Copenhagen',
  estonia: 'Europe/Tallinn',
  finland: 'Europe/Helsinki',
  france: 'Europe/Paris',
  georgia: 'Asia/Tbilisi',
  germany: 'Europe/Berlin',
  greece: 'Europe/Athens',
  hungary: 'Europe/Budapest',
  iceland: 'Atlantic/Reykjavik',
  ireland: 'Europe/Dublin',
  italy: 'Europe/Rome',
  latvia: 'Europe/Riga',
  lithuania: 'Europe/Vilnius',
  luxembourg: 'Europe/Luxembourg',
  malta: 'Europe/Malta',
  moldova: 'Europe/Chisinau',
  montenegro: 'Europe/Podgorica',
  netherlands: 'Europe/Amsterdam',
  'north macedonia': 'Europe/Skopje',
  norway: 'Europe/Oslo',
  poland: 'Europe/Warsaw',
  portugal: 'Europe/Lisbon',
  romania: 'Europe/Bucharest',
  serbia: 'Europe/Belgrade',
  slovakia: 'Europe/Bratislava',
  slovenia: 'Europe/Ljubljana',
  spain: 'Europe/Madrid',
  sweden: 'Europe/Stockholm',
  switzerland: 'Europe/Zurich',
  ukraine: 'Europe/Kyiv',
  'united kingdom': 'Europe/London',

  // --- Asia & Middle East ---
  afghanistan: 'Asia/Kabul',
  bahrain: 'Asia/Bahrain',
  bangladesh: 'Asia/Dhaka',
  burma: 'Asia/Yangon',
  myanmar: 'Asia/Yangon',
  cambodia: 'Asia/Phnom_Penh',
  china: 'Asia/Shanghai', // officially a single zone
  'gaza strip': 'Asia/Gaza',
  'hong kong': 'Asia/Hong_Kong',
  india: 'Asia/Kolkata',
  iran: 'Asia/Tehran',
  iraq: 'Asia/Baghdad',
  israel: 'Asia/Jerusalem',
  japan: 'Asia/Tokyo',
  jordan: 'Asia/Amman',
  'korea, north': 'Asia/Pyongyang',
  'korea, south': 'Asia/Seoul',
  'south korea': 'Asia/Seoul',
  kuwait: 'Asia/Kuwait',
  kyrgyzstan: 'Asia/Bishkek',
  laos: 'Asia/Vientiane',
  lebanon: 'Asia/Beirut',
  macau: 'Asia/Macau',
  malaysia: 'Asia/Kuala_Lumpur',
  mongolia: 'Asia/Ulaanbaatar',
  nepal: 'Asia/Kathmandu',
  oman: 'Asia/Muscat',
  pakistan: 'Asia/Karachi',
  philippines: 'Asia/Manila',
  qatar: 'Asia/Qatar',
  'saudi arabia': 'Asia/Riyadh',
  singapore: 'Asia/Singapore',
  'sri lanka': 'Asia/Colombo',
  syria: 'Asia/Damascus',
  taiwan: 'Asia/Taipei',
  tajikistan: 'Asia/Dushanbe',
  thailand: 'Asia/Bangkok',
  turkey: 'Europe/Istanbul',
  turkmenistan: 'Asia/Ashgabat',
  'united arab emirates': 'Asia/Dubai',
  uzbekistan: 'Asia/Tashkent',
  vietnam: 'Asia/Ho_Chi_Minh',
  yemen: 'Asia/Aden',

  // --- Africa ---
  algeria: 'Africa/Algiers',
  angola: 'Africa/Luanda',
  benin: 'Africa/Porto-Novo',
  botswana: 'Africa/Gaborone',
  'burkina faso': 'Africa/Ouagadougou',
  burundi: 'Africa/Bujumbura',
  cameroon: 'Africa/Douala',
  'central african republic': 'Africa/Bangui',
  chad: 'Africa/Ndjamena',
  'congo (brazzaville)': 'Africa/Brazzaville',
  'congo (kinshasa)': 'Africa/Kinshasa',
  "côte d’ivoire": 'Africa/Abidjan',
  "cote d'ivoire": 'Africa/Abidjan',
  djibouti: 'Africa/Djibouti',
  egypt: 'Africa/Cairo',
  eritrea: 'Africa/Asmara',
  eswatini: 'Africa/Mbabane',
  ethiopia: 'Africa/Addis_Ababa',
  gabon: 'Africa/Libreville',
  'gambia, the': 'Africa/Banjul',
  gambia: 'Africa/Banjul',
  ghana: 'Africa/Accra',
  guinea: 'Africa/Conakry',
  'guinea-bissau': 'Africa/Bissau',
  kenya: 'Africa/Nairobi',
  lesotho: 'Africa/Maseru',
  liberia: 'Africa/Monrovia',
  libya: 'Africa/Tripoli',
  madagascar: 'Indian/Antananarivo',
  malawi: 'Africa/Blantyre',
  mali: 'Africa/Bamako',
  mauritania: 'Africa/Nouakchott',
  mauritius: 'Indian/Mauritius',
  morocco: 'Africa/Casablanca',
  mozambique: 'Africa/Maputo',
  namibia: 'Africa/Windhoek',
  niger: 'Africa/Niamey',
  nigeria: 'Africa/Lagos',
  rwanda: 'Africa/Kigali',
  senegal: 'Africa/Dakar',
  'sierra leone': 'Africa/Freetown',
  somalia: 'Africa/Mogadishu',
  'south africa': 'Africa/Johannesburg',
  'south sudan': 'Africa/Juba',
  sudan: 'Africa/Khartoum',
  tanzania: 'Africa/Dar_es_Salaam',
  togo: 'Africa/Lome',
  tunisia: 'Africa/Tunis',
  uganda: 'Africa/Kampala',
  zambia: 'Africa/Lusaka',
  zimbabwe: 'Africa/Harare',

  // --- Oceania ---
  fiji: 'Pacific/Fiji',
  'new zealand': 'Pacific/Auckland',
  'papua new guinea': 'Pacific/Port_Moresby',
};

/** Countries whose zones we resolve by longitude because they span several. */
const COUNTRY_SPLIT_ZONE: Record<string, (lat: number, lng: number) => string | null> = {
  brazil: (_lat, lng) => {
    if (lng < -67.8) return 'America/Rio_Branco';
    if (lng < -57) return 'America/Manaus';
    return 'America/Sao_Paulo';
  },
  chile: (lat) => (lat < -50 ? 'America/Punta_Arenas' : 'America/Santiago'),
  mexico: (_lat, lng) => {
    if (lng < -113) return 'America/Tijuana';
    if (lng < -105.5) return 'America/Hermosillo';
    if (lng < -100.5) return 'America/Chihuahua';
    if (lng > -89.5) return 'America/Cancun';
    return 'America/Mexico_City';
  },
  australia: (lat, lng) => {
    if (lng < 129) return 'Australia/Perth';
    if (lng < 138) return lat < -26 ? 'Australia/Adelaide' : 'Australia/Darwin';
    if (lat < -39.5) return 'Australia/Hobart';
    if (lng > 150.5 && lat > -25) return 'Australia/Brisbane';
    return 'Australia/Sydney';
  },
  indonesia: (_lat, lng) => {
    if (lng < 112) return 'Asia/Jakarta';
    if (lng < 128) return 'Asia/Makassar';
    return 'Asia/Jayapura';
  },
  kazakhstan: (_lat, lng) => (lng < 63 ? 'Asia/Aqtobe' : 'Asia/Almaty'),
  russia: (_lat, lng) => {
    if (lng < 30) return 'Europe/Kaliningrad';
    if (lng < 45) return 'Europe/Moscow';
    if (lng < 57) return 'Europe/Samara';
    if (lng < 72) return 'Asia/Yekaterinburg';
    if (lng < 83) return 'Asia/Omsk';
    if (lng < 90) return 'Asia/Novosibirsk';
    if (lng < 103) return 'Asia/Krasnoyarsk';
    if (lng < 115) return 'Asia/Irkutsk';
    if (lng < 129) return 'Asia/Yakutsk';
    if (lng < 141) return 'Asia/Vladivostok';
    if (lng < 155) return 'Asia/Magadan';
    return 'Asia/Kamchatka';
  },
};

const UNITED_STATES = new Set(['united states', 'united states of america', 'usa', 'us', 'u.s.', 'u.s.a.']);
const CANADA = new Set(['canada', 'ca']);

/** Geocoded rows store two-letter state codes, so expand them before lookup. */
const US_STATE_BY_ABBR: Record<string, string> = {
  al: 'alabama', ak: 'alaska', az: 'arizona', ar: 'arkansas', ca: 'california',
  co: 'colorado', ct: 'connecticut', de: 'delaware', dc: 'district of columbia',
  fl: 'florida', ga: 'georgia', hi: 'hawaii', id: 'idaho', il: 'illinois',
  in: 'indiana', ia: 'iowa', ks: 'kansas', ky: 'kentucky', la: 'louisiana',
  me: 'maine', md: 'maryland', ma: 'massachusetts', mi: 'michigan', mn: 'minnesota',
  ms: 'mississippi', mo: 'missouri', mt: 'montana', ne: 'nebraska', nv: 'nevada',
  nh: 'new hampshire', nj: 'new jersey', nm: 'new mexico', ny: 'new york',
  nc: 'north carolina', nd: 'north dakota', oh: 'ohio', ok: 'oklahoma',
  or: 'oregon', pa: 'pennsylvania', pr: 'puerto rico', ri: 'rhode island',
  sc: 'south carolina', sd: 'south dakota', tn: 'tennessee', tx: 'texas',
  ut: 'utah', vt: 'vermont', va: 'virginia', wa: 'washington',
  wv: 'west virginia', wi: 'wisconsin', wy: 'wyoming',
};

const CANADA_PROVINCE_BY_ABBR: Record<string, string> = {
  ab: 'alberta', bc: 'british columbia', mb: 'manitoba', nb: 'new brunswick',
  nl: 'newfoundland and labrador', ns: 'nova scotia', on: 'ontario',
  pe: 'prince edward island', qc: 'quebec', sk: 'saskatchewan', yt: 'yukon',
};

function normalize(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function expandRegion(region: string, abbreviations: Record<string, string>): string {
  if (region.length !== 2) return region;
  return abbreviations[region] ?? region;
}

/**
 * Resolve an IANA zone from a place. Returns null when the region is
 * multi-zone and we have no coordinates to disambiguate.
 */
export function resolveIanaTimeZone(
  country: unknown,
  region: unknown,
  latitude?: number | null,
  longitude?: number | null,
): string | null {
  const countryKey = normalize(country);
  const rawRegion = normalize(region);
  const hasCoords = typeof latitude === 'number' && typeof longitude === 'number';

  if (UNITED_STATES.has(countryKey) || (!countryKey && US_SINGLE_ZONE[expandRegion(rawRegion, US_STATE_BY_ABBR)])) {
    const regionKey = expandRegion(rawRegion, US_STATE_BY_ABBR);
    const single = US_SINGLE_ZONE[regionKey];
    if (single) return single;
    const split = US_SPLIT_ZONE[regionKey];
    if (split && hasCoords) return split(latitude as number, longitude as number);
    return null;
  }

  if (CANADA.has(countryKey)) {
    return CANADA_ZONE[expandRegion(rawRegion, CANADA_PROVINCE_BY_ABBR)] ?? null;
  }

  const splitCountry = COUNTRY_SPLIT_ZONE[countryKey];
  if (splitCountry) {
    return hasCoords ? splitCountry(latitude as number, longitude as number) : null;
  }

  return COUNTRY_ZONE[countryKey] ?? null;
}

/**
 * Short zone abbreviation ('PDT', 'EST', 'GMT-3') for the supplied wall-clock
 * date. Returns null when the runtime cannot format the zone.
 */
export function getTimeZoneAbbreviation(timeZone: string | null, wallClockValue?: string | null): string | null {
  if (!timeZone) return null;
  const parsed = parseEventLocalDateTime(wallClockValue);
  // Approximating the instant with the wall-clock fields read as UTC is within
  // a day of the truth, which is enough to land on the right side of a DST
  // transition for every date except the transition day itself.
  const instant = parsed
    ? new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day, 12))
    : new Date();

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(instant);
    const name = parts.find((p) => p.type === 'timeZoneName')?.value;
    return name || null;
  } catch {
    return null;
  }
}

interface TimeZoneSource {
  isVirtual?: boolean | null;
  venue?: string | EventVenue | null;
  location?: EventLocation | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
}

/**
 * Resolve the zone to label an event's times with. Returns an all-null result
 * when nothing reliable is available — callers then render the wall clock
 * without an abbreviation instead of inventing one.
 */
export function resolveEventTimeZone(
  event: TimeZoneSource | null | undefined,
  format: EventFormat,
  referenceDate?: string | null,
): ResolvedTimeZone {
  if (!event) return UNRESOLVED;

  // A virtual-only event has no venue, and no declared zone is stored, so we
  // have nothing honest to show.
  if (format === 'virtual') return UNRESOLVED;

  const venue = typeof event.venue === 'object' && event.venue ? (event.venue as EventVenue) : null;
  const venueLocation = venue?.location ?? null;

  const candidates: Array<{ source: 'venue' | 'location'; zone: string | null }> = [
    {
      source: 'venue',
      zone: resolveIanaTimeZone(
        venueLocation?.country ?? event.country,
        venueLocation?.state ?? event.state,
        venue?.latitude ?? venueLocation?.latitude,
        venue?.longitude ?? venueLocation?.longitude,
      ),
    },
    {
      source: 'location',
      zone: resolveIanaTimeZone(
        event.location?.country ?? event.country,
        event.location?.state ?? event.state,
        event.location?.latitude,
        event.location?.longitude,
      ),
    },
  ];

  for (const candidate of candidates) {
    if (!candidate.zone) continue;
    const abbreviation = getTimeZoneAbbreviation(candidate.zone, referenceDate);
    if (!abbreviation) continue;
    return { timeZone: candidate.zone, abbreviation, source: candidate.source };
  }

  return UNRESOLVED;
}

/** True when the value carries a time we are willing to render. */
export function shouldShowTime(value?: string | null): boolean {
  return hasKnownEventTime(value);
}
